import {
  createEffect,
  createResource,
  createMemo,
  on,
  mergeProps,
  onCleanup,
} from "solid-js";
import {
  CorrectComponentType,
  customShadowlessElement,
  correctElementType,
} from "@brytdesigns/web-component-core/utils";
import {
  createContext,
  provide,
  consume,
  ICustomElement,
} from "component-register";
import throttle from "lodash.throttle";

type Props = {
  searchUrl?: string;
  sectionId: string;
  query?: string;
  limit?: number;
  scope?: string;
  types?: string[];
  searchableFields?: string;
  unavailableProducts?: string;
  delay: number;
};

type SearchState = {
  element: HTMLElement & ICustomElement;
} & Props;

const PredictiveSearchContext = createContext((state: SearchState) => {
  const url = createMemo(() => {
    const url = new URL(window.location.origin + state.searchUrl);
    if (state.limit)
      url.searchParams.append("resources[limit]", `${state.limit}`);
    if (state.scope)
      url.searchParams.append("resources[limit_scope]", state.scope);
    if (state.types)
      url.searchParams.append("resources[type]", state.types.join(","));
    if (state.unavailableProducts)
      url.searchParams.append(
        "resources[options][unavailable_products]",
        state.unavailableProducts,
      );

    url.searchParams.append("q", state.query || "");
    url.searchParams.append("section_id", state.sectionId);

    return url.pathname + url.search;
  });

  const [textResults] = createResource(url, async (url) => {
    const res = await fetch(url);
    const text = await res.text();
    return text;
  });

  const getResultsMarkup = () => {
    const text = textResults();
    if (!text) return "";

    const doc = new DOMParser().parseFromString(text, "text/html");
    const resultsElement = doc.querySelector<HTMLElement>(
      `#shopify-section-${state.sectionId} predictive-search-results`,
    );

    if (!resultsElement) return "";
    return resultsElement.innerHTML;
  };

  const createQueryUpdater = () =>
    throttle((query: string) => {
      if (query === state.query) return;
      state.element.setAttribute("query", query);
    }, state.delay);

  return [
    {
      textResults,
      query: () => state.query,
    },
    {
      getResultsMarkup,
      createQueryUpdater,
    },
  ];
});

const usePredictiveSearch = (element: ICustomElement & HTMLElement) => {
  const context = consume(PredictiveSearchContext, element);
  if (!context) throw new Error("predictive-search: context is required");
  return context;
};

const PredictiveSearch: CorrectComponentType<Props> = (props, { element }) => {
  if (!props.searchUrl)
    return console.warn("predictive-search: searchUrl is required");
  if (!props.sectionId)
    return console.warn("predictive-search: sectionId is required");

  provide(PredictiveSearchContext, mergeProps(props, { element }), element);
};

const PredictiveSearchListener: CorrectComponentType<{
  on: string;
  preventDefault: boolean;
}> = (props, { element }) => {
  const [_, { createQueryUpdater }] = usePredictiveSearch(element);

  createEffect(
    on(
      () => ({
        on: props.on,
        preventDefault: props.preventDefault,
        updateQuery: createQueryUpdater(),
      }),
      ({ on, preventDefault, updateQuery }) => {
        if (!on)
          return console.warn("predictive-search-listener: on is required");
        const handleEvent = (event: Event) => {
          if (preventDefault) event.preventDefault();
          if (on === "submit") {
            const formData = new FormData(event.target as HTMLFormElement);
            const formObj = Object.fromEntries(formData.entries());
            return updateQuery(formObj.q);
          }
          return updateQuery((event.target as HTMLInputElement).value || "");
        };
        element.addEventListener(on, handleEvent);
        return onCleanup(() => element.removeEventListener(on, handleEvent));
      },
    ),
  );
};

const PredictiveSearchResults: CorrectComponentType<{}> = (
  _props,
  { element },
) => {
  const [state, { getResultsMarkup }] = usePredictiveSearch(element);

  createEffect(
    on(
      () => state.textResults(),
      () => {
        element.innerHTML = getResultsMarkup();
      },
    ),
  );
};

customShadowlessElement(
  "predictive-search",
  {
    sectionId: "",
    query: "",
    limit: undefined,
    scope: undefined,
    types: undefined,
    unavailableProducts: undefined,
    searchUrl: "",
    delay: 500,
  },
  correctElementType(PredictiveSearch),
);

customShadowlessElement(
  "predictive-search-listener",
  {
    on: "submit",
    preventDefault: true,
  },
  correctElementType(PredictiveSearchListener),
);

customShadowlessElement(
  "predictive-search-results",
  {},
  correctElementType(PredictiveSearchResults),
);
